/**
 * Cart page wrapper — re-renders itself from the section HTML that cart.js
 * bundles into every cart mutation ('cart:change' event), and owns the one
 * cart action the engine has no endpoint for: clearing every line at once.
 *
 * Both listeners are delegated from the host element, so they survive the
 * innerHTML swap in `onCartChange`.
 * @extends HTMLElement
 */
export class CartPage extends HTMLElement {
  constructor() {
    super();
    this.onCartChange = this.onCartChange.bind(this);
    this.onClick = this.onClick.bind(this);
  }

  connectedCallback() {
    document.addEventListener('cart:change', this.onCartChange);
    this.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    document.removeEventListener('cart:change', this.onCartChange);
    this.removeEventListener('click', this.onClick);
  }

  get sectionId() {
    return this.closest('.shopify-section')?.id.replace('shopify-section-', '');
  }

  onCartChange(event) {
    const html = event.detail.sections?.[this.sectionId];
    if (!html) return;

    const next = new DOMParser().parseFromString(html, 'text/html').querySelector('cart-page');
    if (next) this.innerHTML = next.innerHTML;
  }

  onClick(event) {
    const clearButton = event.target.closest('[data-clear-cart]');
    if (!clearButton) return;

    event.preventDefault();
    this.clear(clearButton);
  }

  /**
   * Sets every line to quantity 0 in a single `/cart/update.js` call.
   *
   * Keyed by line item key rather than line index so the payload can't be
   * thrown off by a concurrent mutation reordering the lines. No disabled
   * toggling needed: cart.js serializes mutations through its queue and marks
   * the document `cart-busy` for the duration, and a successful clear replaces
   * this markup with the empty state.
   *
   * @param {HTMLElement} source - The button that triggered the clear.
   * @returns {Promise<void>}
   */
  async clear(source) {
    const updates = {};
    for (const item of this.querySelectorAll('.cart-item[data-line-key]')) {
      updates[item.dataset.lineKey] = 0;
    }

    if (Object.keys(updates).length === 0) return;

    try {
      await window.Cart.update({ updates }, source);
    } catch (error) {
      console.error('Clearing the cart failed:', error.message);
    }
  }
}

if (!customElements.get('cart-page')) {
  customElements.define('cart-page', CartPage);
}
